import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCollaboratorsOverview } from "@/api/collaborator";
import { useSessionState } from "@/hooks/useSessionState";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";

import {
  HandHeart,
  Search,
  DollarSign,
  Users,
  TrendingUp,
  Loader2,
  ExternalLink,
  Tag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePagination } from "@/hooks/usePagination";

interface CollaboratorSummary {
  id: number;
  uid: string;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  totalEarnings: number;
  pendingEarnings: number;
  activePromotions: number;
  totalPromotions: number;
  lastActivity: string | null;
  totalPayouts: number;
  pendingPayouts: number;
}

const CollaboratorsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [collaborators, setCollaborators] = useState<CollaboratorSummary[]>([]);
  const [filteredCollaborators, setFilteredCollaborators] = useState<
    CollaboratorSummary[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useSessionState("collaboratorsList.searchTerm", "");
  const [currentPage, setCurrentPage] = useSessionState("collaboratorsList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("collaboratorsList.pageSize", 10);

  // Fetch collaborators overview (for cards)
  const { data: collaboratorsOverview } = useQuery({
    queryKey: ["collaboratorsOverview"],
    queryFn: fetchCollaboratorsOverview,
  });

  // Fetch collaborators data
  const fetchCollaborators = async () => {
    setIsLoading(true);
    try {
      // First, get all users who are assigned as collaborators
      const { data: promotionsData, error: promotionsError } = await supabase
        .from("promotions")
        .select("collaborator_id, is_active, created_at")
        .not("collaborator_id", "is", null);

      if (promotionsError) throw promotionsError;

      // Get unique collaborator IDs
      const collaboratorIds = [
        ...new Set(promotionsData.map((p) => p.collaborator_id)),
      ];

      if (collaboratorIds.length === 0) {
        setCollaborators([]);
        setFilteredCollaborators([]);
        setIsLoading(false);
        return;
      }

      // Fetch user profiles for collaborators
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, uid, name, email, avatar_url")
        .in("id", collaboratorIds);

      if (profilesError) throw profilesError;

      // Fetch earnings data for all collaborators
      const { data: earningsData, error: earningsError } = await supabase
        .from("collaborator_earnings")
        .select("collaborator_id, amount_earned, payout_id, created_at")
        .in("collaborator_id", collaboratorIds);

      if (earningsError) throw earningsError;

      // Fetch payouts data for all collaborators
      const { data: payoutsData, error: payoutsError } = await supabase
        .from("collaborator_payouts")
        .select("collaborator_id, amount, status, created_at")
        .in("collaborator_id", collaboratorIds);

      if (payoutsError) throw payoutsError;

      // Calculate stats for each collaborator
      const collaboratorSummaries: CollaboratorSummary[] = profilesData.map(
        (profile) => {
          const userPromotions = promotionsData.filter(
            (p) => p.collaborator_id === profile.id
          );
          const userEarnings = earningsData.filter(
            (e) => e.collaborator_id === profile.id
          );
          const userPayouts = payoutsData.filter(
            (p) => p.collaborator_id === profile.id
          );

          const totalEarnings = userEarnings.reduce(
            (sum, e) => sum + (e.amount_earned || 0),
            0
          );
          const pendingEarnings = userEarnings
            .filter((e) => !e.payout_id)
            .reduce((sum, e) => sum + (e.amount_earned || 0), 0);

          const totalPayouts = userPayouts.reduce(
            (sum, p) => sum + (p.amount || 0),
            0
          );
          const pendingPayouts = userPayouts
            .filter((p) => p.status === "pending")
            .reduce((sum, p) => sum + (p.amount || 0), 0);

          const activePromotions = userPromotions.filter(
            (p) => p.is_active
          ).length;

          // Find last activity (most recent earning or promotion creation)
          const allDates = [
            ...userEarnings.map((e) => e.created_at),
            ...userPromotions.map((p) => p.created_at),
          ].filter(Boolean);
          const lastActivity =
            allDates.length > 0 ? allDates.sort().reverse()[0] : null;

          return {
            ...profile,
            totalEarnings,
            pendingEarnings,
            activePromotions,
            totalPromotions: userPromotions.length,
            lastActivity,
            totalPayouts,
            pendingPayouts,
          };
        }
      );

      // Sort by total earnings (highest first)
      collaboratorSummaries.sort((a, b) => b.totalEarnings - a.totalEarnings);

      setCollaborators(collaboratorSummaries);
      setFilteredCollaborators(collaboratorSummaries);

      // Note: Total stats are now fetched separately via collaboratorsOverview query
    } catch (error) {
      console.error("Error fetching collaborators:", error);
      toast({
        title: "Error",
        description: "Failed to load collaborators data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Skip page reset on first render (preserves restored page from sessionStorage)
  const isFirstRender = useRef(true);

  // Filter collaborators based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCollaborators(collaborators);
    } else {
      const filtered = collaborators.filter(
        (collaborator) =>
          collaborator.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          collaborator.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCollaborators(filtered);
    }
    // Reset to first page when search changes (skip on first render)
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, collaborators]);

  useEffect(() => {
    fetchCollaborators();
  }, []);

  // Format currency
  const formatCurrency = (amount: number) => {
    return `₹${amount.toFixed(2)}`;
  };

  // Calculate pagination
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: filteredCollaborators.length,
    itemsPerPage: pageSize,
    currentPage,
  });

  // Get current page items
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentCollaborators = filteredCollaborators.slice(
    startIndex,
    endIndex
  );

  if (isLoading) {
    return (
      <AdminLayout title="Collaborators">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Collaborators">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2">
          <HandHeart className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Collaborators</h1>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Collaborators
                  </p>
                  <p className="text-2xl font-bold">
                    {collaboratorsOverview?.totalCollaborators || 0}
                  </p>
                </div>
                <Users className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Earnings
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(collaboratorsOverview?.totalEarnings || 0)}
                  </p>
                </div>
                <DollarSign className="h-5 w-5 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Pending Earnings
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      collaboratorsOverview?.totalPendingEarnings || 0
                    )}
                  </p>
                </div>
                <DollarSign className="h-5 w-5 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Active Promotions
                  </p>
                  <p className="text-2xl font-bold">
                    {collaboratorsOverview?.totalActivePromotions || 0}
                  </p>
                </div>
                <TrendingUp className="h-5 w-5 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search collaborators..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Collaborators Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Collaborators</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredCollaborators.length === 0 ? (
              <div className="text-center py-8">
                <HandHeart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  No Collaborators Found
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {collaborators.length === 0
                    ? "No users have been assigned as collaborators yet. Create a promotion and assign a collaborator to get started."
                    : "No collaborators match your search criteria."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Collaborator</TableHead>
                      <TableHead>Total Earnings</TableHead>
                      <TableHead>Pending</TableHead>
                      <TableHead>Promotions</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentCollaborators.map((collaborator) => (
                      <TableRow key={collaborator.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage
                                src={collaborator.avatar_url || undefined}
                              />
                              <AvatarFallback>
                                {collaborator.name?.charAt(0) || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">
                                {collaborator.name || "Unknown User"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {collaborator.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(collaborator.totalEarnings)}
                        </TableCell>
                        <TableCell>
                          <span className="text-amber-600">
                            {formatCurrency(collaborator.pendingEarnings)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                collaborator.activePromotions > 0
                                  ? "default"
                                  : "outline"
                              }
                              className={
                                collaborator.activePromotions > 0
                                  ? "bg-green-500"
                                  : ""
                              }
                            >
                              {collaborator.activePromotions} active
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              / {collaborator.totalPromotions} total
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {collaborator.lastActivity ? (
                            <span className="text-sm">
                              {format(
                                new Date(collaborator.lastActivity),
                                "MMM dd, yyyy"
                              )}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              Never
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/admin/collaborator/${collaborator.id}`
                                )
                              }
                            >
                              Dashboard
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigate(`/admin/users/${collaborator.id}`)
                              }
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Profile
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            {filteredCollaborators.length > 0 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Showing{" "}
                  <span className="font-medium">
                    {filteredCollaborators.length === 0
                      ? 0
                      : (currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(
                      currentPage * pageSize,
                      filteredCollaborators.length
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium">
                    {filteredCollaborators.length}
                  </span>{" "}
                  collaborators
                </div>
                <div className="flex items-center gap-4">
                  {totalPages > 0 && (
                    <div className="flex items-center">
                      <Button
                        className="mr-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={!canGoPrev}
                      >
                        First
                      </Button>

                      <Button
                        className="mr-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                        }
                        disabled={!canGoPrev}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      {pageNumbers.map((pageNum) => (
                        <Button
                          key={pageNum}
                          className="mx-0.5 w-8 h-8 p-0"
                          variant={
                            pageNum === currentPage ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      ))}

                      <Button
                        className="ml-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(
                            currentPage < totalPages
                              ? currentPage + 1
                              : totalPages
                          )
                        }
                        disabled={!canGoNext}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>

                      <Button
                        className="ml-1 px-2"
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={!canGoNext}
                      >
                        Last
                      </Button>
                    </div>
                  )}

                  <select
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing page size
                    }}
                  >
                    {[10, 20, 30, 50].map((size) => (
                      <option key={size} value={size}>
                        {size} per page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default CollaboratorsList;
 